import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { type QuoteDto, useGetShipment } from '@uturn/api/v3';
import { formatPrice } from '@uturn/portal/utils';
import {
	Avatar,
	AvatarFallback,
	Badge,
	Button,
	Card,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Icon,
	Separator,
	Stars,
	Textarea,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@uturn/ui-kit';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CharacterHint } from '../character-hint';

const MAX_DECLINE_REASON = 250;

const useShipmentsCompletedFormatter = () => {
	const { t } = useTranslation();

	const format = (shipmentsCompleted: number) => {
		if (shipmentsCompleted >= 0 && shipmentsCompleted <= 50) {
			return '0-50';
		}

		if (shipmentsCompleted >= 51 && shipmentsCompleted <= 200) {
			return '51-200';
		}

		if (shipmentsCompleted >= 201 && shipmentsCompleted <= 1000) {
			return '201-1000';
		}

		if (shipmentsCompleted >= 1001) {
			return '1000+';
		}

		return t('general.na', 'N/A');
	};

	return {
		format,
	};
};

type QuotePriceDetailsProps = {
	targetPriceFormatted: string;
	feeFormatted: string;
	totalPriceFormatted: string;
};

const QuotePriceDetails = ({
	targetPriceFormatted,
	feeFormatted,
	totalPriceFormatted,
}: QuotePriceDetailsProps) => {
	const { t } = useTranslation();

	return (
		<div className="mb-4 text-sm">
			<div className="text-muted-foreground flex justify-between">
				<p className="text-muted-foreground">
					{t(
						'pages.create_shipment.summary_section.target_price.label',
						'Quote price',
					)}
				</p>
				<span>{targetPriceFormatted}</span>
			</div>
			<div className="text-muted-foreground flex justify-between">
				<p>{t('pages.create_shipment.summary_section.fee.label', 'Fee')}</p>
				<span>{feeFormatted}</span>
			</div>

			<Separator className="my-2" />

			<div className="flex items-center justify-between">
				<p className="font-bold">
					{t(
						'pages.create_shipment.summary_section.total_price.label',
						'Total price',
					)}
				</p>
				<span className="font-bold">
					<span className="font-bold">{totalPriceFormatted}</span>
				</span>
			</div>
		</div>
	);
};

export function SingleQuote({
	onAssignQuote,
	onDeclineQuote,
	quote,
	shipmentNumber,
	defaultOpen = false,
}: {
	onAssignQuote: (quoteId: number) => void;
	onDeclineQuote: (quoteId: number, declineReason?: string) => void;
	quote: QuoteDto;
	shipmentNumber: number;
	defaultOpen?: boolean;
}) {
	const { data } = useGetShipment(shipmentNumber);
	const { t } = useTranslation();
	const [open, setOpen] = useState(defaultOpen);
	const [isDeclinePending, setIsDeclinePending] = useState(false);
	const [isBreakdownOpen, setisBreakdownOpen] = useState(false);
	const [declineReason, setDeclineReason] = useState('');
	const { format } = useShipmentsCompletedFormatter();

	const formattedPrices = quote
		? {
				targetPrice: formatPrice(
					quote?.price?.quantity ?? 0,
					quote?.price?.currencyCode ?? 'EUR',
				),
				feeValue: formatPrice(
					quote?.shipmentFee?.quantity ?? 0,
					quote?.shipmentFee?.currencyCode ?? 'EUR',
				),
				totalPrice: formatPrice(
					quote?.quotedPriceIncludingFee?.quantity ?? 0,
					quote?.quotedPriceIncludingFee?.currencyCode ?? 'EUR',
				),
			}
		: null;

	return (
		<AnimatePresence mode="wait">
			<Card className="p-4">
				<div className="flex items-center justify-between gap-4">
					<div className="min-w-0 flex-1">
						<div className="flex gap-2">
							<div className="relative">
								<Avatar className="size-3rem">
									<AvatarFallback
									name={quote.carrier.name}
									className="bg-foreground text-primary dark:bg-primary dark:text-primary-foreground"
								/>
								</Avatar>
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<div className="bg-primary absolute bottom-0 right-[-4px] flex size-4 items-center justify-center rounded-full border border-white">
												<Icon className="text-black" size="xs" icon={faCheck} />
											</div>
										</TooltipTrigger>
										<TooltipContent side="bottom">
											This carrier is verified
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<div className="min-w-0">
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<h2 className="truncate">{quote.carrier.name}</h2>
										</TooltipTrigger>
										<TooltipContent>{quote.carrier.name}</TooltipContent>
									</Tooltip>
								</TooltipProvider>
								<div className="flex items-center gap-1">
									<Stars
										size="sm"
										rating={Math.round(quote.carrier.rating.averageRating)}
									/>
									<span className="text-sm">
										({quote.carrier.rating.totalRatings})
									</span>
								</div>
							</div>
						</div>
					</div>
					<div className="min-w-0">
						<div className="flex">
							<Button
								size="xs"
								customRadius
								className="rounded-s-md rounded-e-none"
								onClick={() => onAssignQuote(quote.id as number)}
							>
								{t(
									'shipment.quote.assign-for-total-price',
									'Assign total {{price}}',
									{
										price: formattedPrices?.totalPrice,
									},
								)}
							</Button>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										size="xs"
										customRadius
										className="border-l rounded-e-md rounded-s-none"
									>
										<Icon icon={faEllipsisVertical} />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" className="text-sm">
									<DropdownMenuItem
										onClick={() => {
											setisBreakdownOpen(true);
											setIsDeclinePending(false);
										}}
									>
										{t(
											'shipment.quote.view-price-breakdown',
											'View price breakdown',
										)}
									</DropdownMenuItem>

									<DropdownMenuItem
										className="text-red-600"
										onClick={() => {
											setIsDeclinePending(true);
											setisBreakdownOpen(false);
										}}
									>
										{t('shipment.quote.decline-quote', 'Decline quote')}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					</div>
				</div>

				<AnimatePresence>
					{isBreakdownOpen && (
						<motion.div
							initial={{ height: 0, opacity: 0 }}
							animate={{ height: 'auto', opacity: 1 }}
							exit={{ height: 0, opacity: 0 }}
						>
							<div className="pt-4">
								<QuotePriceDetails
									targetPriceFormatted={formattedPrices?.targetPrice ?? 'N/A'}
									feeFormatted={formattedPrices?.feeValue ?? 'N/A'}
									totalPriceFormatted={formattedPrices?.totalPrice ?? 'N/A'}
								/>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
				<AnimatePresence>
					{isDeclinePending && (
						<motion.div
							initial={{ height: 0, opacity: 0 }}
							animate={{ height: 'auto', opacity: 1 }}
							exit={{ height: 0, opacity: 0 }}
						>
							<div className="pt-4">
								<QuotePriceDetails
									targetPriceFormatted={formattedPrices?.targetPrice ?? 'N/A'}
									feeFormatted={formattedPrices?.feeValue ?? 'N/A'}
									totalPriceFormatted={formattedPrices?.totalPrice ?? 'N/A'}
								/>

								<Textarea
									placeholder={t(
										'shipment.quote.decline-reason.placeholder',
										'Reason for declining quote (optional)',
									).toString()}
									value={declineReason}
									onChange={(e) => setDeclineReason(e.target.value)}
									maxLength={MAX_DECLINE_REASON}
								/>
								<CharacterHint
									currentCount={declineReason.length}
									maxCharacters={MAX_DECLINE_REASON}
									translationKey="shipment.quote.decline-reason.hint"
									defaultMessage="You can type up to {{count}} characters."
								/>
								<div className="flex justify-end pt-4">
									<Button
										variant="link"
										onClick={() => setIsDeclinePending(false)}
									>
										{t('shipment.quote.decline-reason.cancel', 'Cancel')}
									</Button>
									<Button
										variant="destructive"
										onClick={() =>
											onDeclineQuote(quote.id as number, declineReason)
										}
									>
										{t('shipment.quote.decline-reason.decline', 'Decline')}
									</Button>
								</div>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
				<Separator className="-mx-4 my-4 w-[calc(100%+32px)]" />
				<div>
					{quote.condition && (
						<div className="mb-2">
							<h3 className="text-muted-foreground text-sm">
								{t('shipment.quote.quote-condition.label', 'Quote condition')}
							</h3>
							<p className="text-md">{quote.condition}</p>
						</div>
					)}
					<AnimatePresence>
						{open && (
							<motion.div
								initial={{ height: 0, opacity: 0 }}
								animate={{ height: 'auto', opacity: 1 }}
								exit={{ height: 0, opacity: 0 }}
							>
								<div className="space-y-2 pb-2">
									<div className="flex">
										<div className="flex-1">
											<h3 className="text-muted-foreground mb-1 text-sm">
												{t(
													'shipment.quote.carrier.active-since.label',
													'Active since',
												)}
											</h3>
											<p className="text-md">{`${new Date(
												quote.carrier.activeSince,
											).getMonth()}/${new Date(
												quote.carrier.activeSince,
											).getFullYear()}`}</p>
										</div>
										<div className="flex-1">
											<h3 className="text-muted-foreground mb-1 text-sm">
												{t(
													'shipment.quote.carrier.shipments.label',
													'Shipments',
												)}
											</h3>
											{format(quote.carrier.shipmentsCompleted)}
										</div>
										<div className="flex-1">
											<h3 className="text-muted-foreground mb-1 text-sm">
												{t(
													'shipment.quote.carrier.capacity.label',
													'Truck Capacity',
												)}
											</h3>
											<p className="text-md">
												{quote.carrier.trucksOwned !== 0
													? quote.carrier.trucksOwned
													: t('general.na', 'N/A')}
											</p>
										</div>
									</div>
									{data?.data.requirements &&
										data.data.requirements.length > 0 && (
											<div>
												<h3 className="text-muted-foreground mb-1 text-sm">
													{t(
														'shipment.quote.carrier.capabilities.label',
														'Capabilities',
													)}
												</h3>
												<div className="flex flex-wrap gap-2">
													{data?.data.requirements.map((requirement) => (
														<Badge
															key={requirement.id}
															className="bg-green-500 hover:bg-green-600"
														>
															<Icon icon={faCheck} className="mr-1" />
															{requirement.description}
														</Badge>
													))}
												</div>
											</div>
										)}
								</div>
							</motion.div>
						)}
					</AnimatePresence>
					<Button
						variant="link"
						className="reveal-carrier-information h-auto p-0"
						onClick={() => setOpen(!open)}
					>
						{!open
							? t(
									'shipment.quote.carrier.more-carrier-information.label',
									'More carrier information',
								)
							: t(
									'shipment.quote.carrier.less-carrier-information.label',
									'Less carrier information',
								)}
					</Button>
				</div>
			</Card>
		</AnimatePresence>
	);
}
